import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Webcam from 'react-webcam';
import { toast } from 'sonner';
import { useQuery } from '@tanstack/react-query';
import { Camera, RotateCcw, ChevronLeft, ChevronRight, Check, X, Sparkles, ZoomIn, Aperture } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { api, ApiError } from '@/lib/api';
import { cn } from '@/lib/utils';
import { GuideOverlay, THREE_D_GUIDES } from '@/features/capture/GuideOverlay';

interface ShotSlot {
  key: string; label: string; instructions: string;
  guide: string; order: number; enabled: boolean;
}

export function CapturePage() {
  const { id: carId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const webcamRef = useRef<Webcam>(null);
  const [idx, setIdx] = useState(0);
  const [facingMode, setFacingMode] = useState<'environment' | 'user'>('environment');
  const [review, setReview] = useState<Blob | null>(null);
  const [reviewUrl, setReviewUrl] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const detectLandscape = () =>
    typeof window !== 'undefined' &&
    (window.matchMedia?.('(orientation: landscape)').matches ??
      window.innerWidth > window.innerHeight);

  const [uiLandscape, setUiLandscape] = useState<boolean>(detectLandscape);
  const [photoAspect, setPhotoAspect] = useState<'landscape' | 'portrait'>(
    () => (detectLandscape() ? 'landscape' : 'portrait'),
  );
  const [aspectLocked, setAspectLocked] = useState(false);

  useEffect(() => {
    const mql = window.matchMedia('(orientation: landscape)');
    const sync = () => {
      const now = detectLandscape();
      setUiLandscape(now);
      if (!aspectLocked) setPhotoAspect(now ? 'landscape' : 'portrait');
    };
    mql.addEventListener?.('change', sync);
    window.addEventListener('resize', sync);
    window.addEventListener('orientationchange', sync);
    return () => {
      mql.removeEventListener?.('change', sync);
      window.removeEventListener('resize', sync);
      window.removeEventListener('orientationchange', sync);
    };
  }, [aspectLocked]);

  // Guide-model zoom — persisted across sessions.
  const [guideZoom, setGuideZoom] = useState<number>(() => {
    if (typeof window === 'undefined') return 180;
    const saved = Number(localStorage.getItem('bgswap:guide-zoom'));
    return Number.isFinite(saved) && saved >= 80 && saved <= 320 ? saved : 180;
  });
  useEffect(() => {
    localStorage.setItem('bgswap:guide-zoom', String(guideZoom));
  }, [guideZoom]);

  // ===== Camera zoom =====================================================
  // Digital zoom only — implemented as a CSS transform on the preview wrapper
  // plus a matching center-crop at capture time. Native MediaStream zoom was
  // unreliable across devices (silently failed on some phones, leaving the
  // preview at 1× while the captured frame got cropped — exactly the
  // preview/capture mismatch users hit). For real optical zoom, the lens
  // picker (Aperture button) cycles through the phone's actual rear cameras
  // (main / ultrawide / telephoto).
  const [camZoom, setCamZoom] = useState<number>(1);            // 1× → 5×
  const MAX_DISPLAY_ZOOM = 5;

  // ===== Lens picker =====================================================
  // Many modern phones expose multiple rear lenses (main / ultrawide /
  // telephoto). Locking by deviceId prevents the orientation toggle (or any
  // unrelated change) from causing the browser to swap lenses on us.
  const [rearCameras, setRearCameras] = useState<MediaDeviceInfo[]>([]);
  const [deviceId, setDeviceIdState] = useState<string | null>(() =>
    typeof window === 'undefined' ? null : localStorage.getItem('bgswap:cam-device-id'),
  );
  function setDeviceId(id: string | null) {
    setDeviceIdState(id);
    if (id) localStorage.setItem('bgswap:cam-device-id', id);
    else localStorage.removeItem('bgswap:cam-device-id');
    setCamZoom(1);
  }

  const refreshDevices = useCallback(async () => {
    try {
      const all = await navigator.mediaDevices.enumerateDevices();
      const videos = all.filter((d) => d.kind === 'videoinput');
      const rears = videos.filter((d) => {
        const l = (d.label ?? '').toLowerCase();
        // Treat anything that isn't obviously front-facing as a rear option.
        return l && !/(front|user|selfie|face)/.test(l);
      });
      setRearCameras(rears.length > 0 ? rears : videos);
      // Auto-select the "main" rear camera if we don't have a saved choice
      // (or saved choice is no longer present).
      const haveSelected = (rears.length > 0 ? rears : videos).some((d) => d.deviceId === deviceId);
      if (!haveSelected) {
        const pool = rears.length > 0 ? rears : videos;
        // Prefer one whose label is just "Back Camera" (no extra qualifiers).
        const main = pool.find((d) => /^back camera$/i.test((d.label ?? '').trim())) ?? pool[0];
        if (main) setDeviceIdState(main.deviceId);
      }
    } catch {
      /* enumerateDevices isn't available pre-permission grant; we'll retry */
    }
  }, [deviceId]);

  useEffect(() => { refreshDevices(); }, [refreshDevices]);

  function handleUserMedia(stream: MediaStream) {
    const track = stream.getVideoTracks()[0];
    const caps = track?.getCapabilities?.() as
      | { focusMode?: string[]; pointsOfInterest?: unknown }
      | undefined;
    setFocusSupported(Array.isArray(caps?.focusMode) && (caps?.focusMode?.length ?? 0) > 0);
    setCamZoom(1);
    refreshDevices();
  }

  function cycleLens() {
    if (rearCameras.length <= 1) return;
    const idx = rearCameras.findIndex((d) => d.deviceId === deviceId);
    const next = rearCameras[(idx + 1) % rearCameras.length];
    if (next) setDeviceId(next.deviceId);
  }


  // Pinch-to-zoom + tap-to-focus on the viewport. The two gestures need to
  // be mutually exclusive: a single tap fires focus only if no second finger
  // touched and the finger didn't move (i.e. wasn't a swipe).
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const pinchRef = useRef<{ dist: number; zoom: number } | null>(null);
  const tapRef = useRef<{ x: number; y: number; t: number; canceled: boolean } | null>(null);
  const [focusSupported, setFocusSupported] = useState(false);
  const [focusMark, setFocusMark] = useState<{ x: number; y: number; key: number } | null>(null);

  async function fireFocusAt(clientX: number, clientY: number) {
    const el = viewportRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const localX = clientX - rect.left;
    const localY = clientY - rect.top;
    if (localX < 0 || localY < 0 || localX > rect.width || localY > rect.height) return;
    setFocusMark({ x: localX, y: localY, key: Date.now() });

    const track = webcamRef.current?.stream?.getVideoTracks?.()[0];
    if (!track) return;
    const nx = localX / rect.width;
    const ny = localY / rect.height;
    // Try the most expressive constraint first; fall back to focusMode-only
    // if pointsOfInterest isn't supported on this device.
    const tryApply = async (constraints: MediaTrackConstraintSet) => {
      try { await track.applyConstraints({ advanced: [constraints] }); return true; }
      catch { return false; }
    };
    const ok = await tryApply({
      focusMode: 'single-shot',
      pointsOfInterest: [{ x: nx, y: ny }],
    } as MediaTrackConstraintSet);
    if (!ok) await tryApply({ focusMode: 'single-shot' } as MediaTrackConstraintSet);
  }

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      const t1 = e.touches[0]!, t2 = e.touches[1]!;
      pinchRef.current = {
        dist: Math.hypot(t1.clientX - t2.clientX, t1.clientY - t2.clientY),
        zoom: camZoom,
      };
      tapRef.current = null; // pinch cancels tap
    } else if (e.touches.length === 1) {
      const t = e.touches[0]!;
      tapRef.current = { x: t.clientX, y: t.clientY, t: Date.now(), canceled: false };
    }
  }, [camZoom]);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2 && pinchRef.current) {
      const t1 = e.touches[0]!, t2 = e.touches[1]!;
      const dist = Math.hypot(t1.clientX - t2.clientX, t1.clientY - t2.clientY);
      const next = Math.max(1, Math.min(MAX_DISPLAY_ZOOM, pinchRef.current.zoom * (dist / pinchRef.current.dist)));
      setCamZoom(next);
    } else if (e.touches.length === 1 && tapRef.current && !tapRef.current.canceled) {
      const t = e.touches[0]!;
      const moved = Math.hypot(t.clientX - tapRef.current.x, t.clientY - tapRef.current.y);
      if (moved > 10) tapRef.current.canceled = true;
    }
  }, []);

  const onTouchEnd = useCallback((e: React.TouchEvent) => {
    pinchRef.current = null;
    const tap = tapRef.current;
    tapRef.current = null;
    // If the last touch ending wasn't a clean tap, ignore.
    if (!tap || tap.canceled) return;
    if (Date.now() - tap.t > 500) return; // long press → ignore
    // If multiple fingers were involved during the gesture, the first call
    // to onTouchStart already cleared tapRef. Trust that.
    if (e.changedTouches.length === 0) return;
    void fireFocusAt(tap.x, tap.y);
  }, []);

  // Auto-clear the focus reticle after the animation finishes.
  useEffect(() => {
    if (!focusMark) return;
    const id = setTimeout(() => setFocusMark(null), 700);
    return () => clearTimeout(id);
  }, [focusMark]);

  const pkg = useQuery({
    queryKey: ['photo-package'],
    queryFn: () => api.get<ShotSlot[]>('/tenant/photo-package'),
  });
  const slots = useMemo(
    () => (pkg.data ?? []).filter((s) => s.enabled).sort((a, b) => a.order - b.order),
    [pkg.data],
  );
  const current = slots[idx];

  const car = useQuery({
    queryKey: ['car', carId],
    queryFn: () => api.get<{ photos: Array<{ slotIndex: number; stage: string }>; pendingJobs?: number }>(`/cars/${carId}`),
    enabled: !!carId,
    // Keep pinging the server while cutouts are still processing so the
    // green checkmarks flip to "clean ready" without a manual refresh.
    refetchInterval: (q) => ((q.state.data?.pendingJobs ?? 0) > 0 ? 3000 : false),
  });
  const done = new Set(car.data?.photos.filter((p) => p.stage === 'ORIGINAL').map((p) => p.slotIndex) ?? []);
  const cleanDone = new Set(car.data?.photos.filter((p) => p.stage === 'CLEAN').map((p) => p.slotIndex) ?? []);

  useEffect(() => {
    if (slots.length && idx >= slots.length) setIdx(slots.length - 1);
  }, [slots.length, idx]);

  useEffect(() => () => { if (reviewUrl) URL.revokeObjectURL(reviewUrl); }, [reviewUrl]);

  async function capture() {
    if (!webcamRef.current || !current) return;
    const snap = webcamRef.current.getScreenshot();
    if (!snap) { toast.error('No frame captured — retry'); return; }
    // 1) Zoom crop — match the CSS preview transform pixel-for-pixel.
    let working = camZoom > 1 ? await cropDataUri(snap, camZoom) : snap;
    // 2) Aspect-ratio crop — the orientation toggle no longer changes the
    //    stream, so we deliver the requested aspect by cropping centrally.
    working = await cropToAspect(working, landscapePhoto ? 16 / 9 : 9 / 16);
    const blob = dataUriToBlob(working);
    if (reviewUrl) URL.revokeObjectURL(reviewUrl);
    setReview(blob);
    setReviewUrl(URL.createObjectURL(blob));
  }

  function discardReview() {
    if (reviewUrl) URL.revokeObjectURL(reviewUrl);
    setReview(null);
    setReviewUrl(null);
  }

  async function confirm() {
    if (!review || !current || !carId) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('slotKey', current.key);
      fd.append('slotIndex', String(idx));
      fd.append('original', review, `${current.key}.jpg`);
      // Background removal is now on-demand — the upload just stores the
      // original. Run the cutout from the car detail page when you're ready.
      await api.post(`/cars/${carId}/photos`, fd);
      toast.success(`${current.label} saved`);
      discardReview();
      await car.refetch();
      if (idx < slots.length - 1) setIdx(idx + 1);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Upload failed');
    } finally {
      setSaving(false);
    }
  }

  if (pkg.isLoading || !current) {
    return <div className="flex min-h-dvh items-center justify-center bg-black text-sm text-muted-foreground">Loading capture…</div>;
  }

  const isLandscape = uiLandscape;
  const landscapePhoto = photoAspect === 'landscape';

  // Stable constraints: lock to a specific device when we know one. We do NOT
  // include width/height/aspectRatio — the orientation toggle no longer
  // re-initializes the stream, so the browser can't decide to switch lenses.
  // Output aspect is enforced post-capture by `cropToAspect` below.
  const videoConstraints: MediaTrackConstraints = useMemo(() => {
    if (deviceId) return { deviceId: { exact: deviceId } };
    return { facingMode };
  }, [deviceId, facingMode]);

  const pendingJobs = car.data?.pendingJobs ?? 0;

  return (
    <div className={cn('fixed inset-0 z-40 flex bg-black text-foreground', isLandscape ? 'flex-row' : 'flex-col')}>
      {/* Top / Left bar */}
      <div className={cn(
        'flex shrink-0 items-center justify-between gap-2 bg-black/90 text-sm',
        isLandscape ? 'w-16 flex-col py-3' : 'h-14 px-3 py-2',
      )}>
        <Button variant="ghost" size="icon" onClick={() => navigate(`/app/cars/${carId}`)}>
          <X className="h-5 w-5" />
        </Button>
        {!isLandscape && (
          <div className="flex-1 text-center">
            <div className="text-[11px] uppercase tracking-widest text-white/60">Photo {idx + 1} of {slots.length}</div>
            <div className="truncate font-medium">{current.label}</div>
          </div>
        )}
        <div className={cn('flex gap-1', isLandscape && 'flex-col')}>
          <Button
            variant="ghost"
            size="icon"
            title="Front / rear camera"
            onClick={() => { setDeviceId(null); setFacingMode((f) => (f === 'environment' ? 'user' : 'environment')); }}
          >
            <RotateCcw className="h-5 w-5" />
          </Button>
          {rearCameras.length > 1 && (
            <Button
              variant="ghost"
              size="icon"
              title={`Lens ${rearCameras.findIndex((d) => d.deviceId === deviceId) + 1}/${rearCameras.length}`}
              onClick={cycleLens}
            >
              <Aperture className="h-5 w-5" />
            </Button>
          )}
          <Button
            variant="ghost" size="icon"
            title={`${aspectLocked ? 'Manual' : 'Auto'} · tap to ${landscapePhoto ? 'portrait' : 'landscape'}`}
            onClick={() => {
              setAspectLocked(true);
              setPhotoAspect((o) => (o === 'landscape' ? 'portrait' : 'landscape'));
            }}
          >
            <OrientationIcon landscape={landscapePhoto} />
          </Button>
        </div>
      </div>

      {/* Viewport — pinch-to-zoom, tap-to-focus */}
      <div
        ref={viewportRef}
        className="relative flex-1 overflow-hidden bg-black"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onClick={(e) => {
          if (e.detail === 0) return;
          if (!('ontouchstart' in window)) void fireFocusAt(e.clientX, e.clientY);
        }}
      >
        {!reviewUrl ? (
          <>
            {/* Zoom wrapper — applied here (not on the Webcam component) so
                the CSS transform reliably hits the underlying <video> on
                every browser. The viewport's overflow-hidden does the crop. */}
            <div
              className="absolute inset-0"
              style={{
                transform: camZoom > 1 ? `scale(${camZoom})` : undefined,
                transformOrigin: 'center center',
                transition: 'transform 60ms ease-out',
                willChange: 'transform',
              }}
            >
              <Webcam
                ref={webcamRef}
                audio={false}
                videoConstraints={videoConstraints}
                screenshotFormat="image/jpeg"
                screenshotQuality={0.92}
                className="absolute inset-0 h-full w-full object-cover"
                onUserMedia={handleUserMedia}
                onUserMediaError={() => toast.error('Camera access denied — check browser settings')}
              />
            </div>
            <GuideOverlay guide={current.guide} shotKey={current.key} zoom={guideZoom} />

            {/* Photo-aspect pill */}
            <div className="absolute right-3 top-3 rounded-full bg-black/60 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-white/90 backdrop-blur">
              {landscapePhoto ? 'Landscape' : 'Portrait'} {aspectLocked ? '· manual' : '· auto'}
            </div>

            {/* Cutout-processing indicator — shows while server jobs are in flight */}
            {pendingJobs > 0 && (
              <div className="absolute left-3 top-3 flex items-center gap-2 rounded-full bg-black/60 px-2.5 py-1 text-[10px] text-white/90 backdrop-blur">
                <Sparkles className="h-3 w-3 animate-pulse text-primary" />
                {pendingJobs} cutout{pendingJobs === 1 ? '' : 's'} processing
              </div>
            )}

            {/* Camera zoom slider (independent of guide-size slider) */}
            <div className="pointer-events-auto absolute right-3 top-1/2 flex -translate-y-1/2 flex-col items-center gap-2 rounded-full bg-black/60 px-2 py-3 backdrop-blur">
              <ZoomIn className="h-3.5 w-3.5 text-white/80" />
              <input
                type="range"
                min={1} max={MAX_DISPLAY_ZOOM} step={0.05}
                value={camZoom}
                onChange={(e) => setCamZoom(Number(e.target.value))}
                className="h-32 w-1 appearance-none accent-cyan-400 [writing-mode:bt-lr] [-webkit-appearance:slider-vertical]"
                style={{ writingMode: 'vertical-lr' as React.CSSProperties['writingMode'] }}
                aria-label="Camera zoom"
              />
              <span className="w-9 text-center text-[10px] tabular-nums text-white/80">{camZoom.toFixed(1)}×</span>
            </div>

            {/* Size slider — exterior (3D) shots only */}
            {THREE_D_GUIDES.has(current.guide) && (
              <div className="pointer-events-auto absolute left-1/2 bottom-16 flex -translate-x-1/2 items-center gap-2 rounded-full bg-black/65 px-3 py-1.5 backdrop-blur">
                <span className="text-[10px] uppercase tracking-wider text-white/70">Size</span>
                <input
                  type="range" min={80} max={320} step={5} value={guideZoom}
                  onChange={(e) => setGuideZoom(Number(e.target.value))}
                  className="h-1 w-40 appearance-none rounded-full bg-white/25 accent-cyan-400"
                  aria-label="Guide model size"
                />
                <span className="w-8 text-right text-[10px] tabular-nums text-white/80">{Math.round((guideZoom / 180) * 100)}%</span>
              </div>
            )}

            <div className="absolute inset-x-3 bottom-3 rounded-lg bg-black/70 px-3 py-2 text-[13px] text-white/95 backdrop-blur">
              {current.instructions}
            </div>

            {/* Tap-to-focus reticle — animates in, holds, fades out */}
            {focusMark && (
              <div
                key={focusMark.key}
                className="pointer-events-none absolute"
                style={{ left: focusMark.x, top: focusMark.y, transform: 'translate(-50%, -50%)' }}
              >
                <div className="focus-reticle h-16 w-16 rounded-md border-2 border-cyan-400" />
              </div>
            )}

            {/* First-tap hint when supported but unused */}
            {focusSupported && !focusMark && (
              <div className="pointer-events-none absolute bottom-16 left-1/2 -translate-x-1/2 rounded-full bg-black/35 px-2 py-0.5 text-[10px] text-white/70 backdrop-blur">
                Tap to focus
              </div>
            )}
          </>
        ) : (
          <img src={reviewUrl} alt="review" className="absolute inset-0 h-full w-full object-contain" />
        )}
      </div>

      {/* Shot strip */}
      <div className={cn(
        'flex shrink-0 gap-1 border-white/5 bg-black/90 p-2',
        isLandscape ? 'w-[72px] flex-col overflow-y-auto overflow-x-hidden border-l' : 'overflow-x-auto border-t',
      )}>
        {slots.map((s, i) => {
          const hasOrig = done.has(i);
          const hasClean = cleanDone.has(i);
          return (
            <button
              key={s.key}
              onClick={() => { discardReview(); setIdx(i); }}
              className={cn(
                'flex shrink-0 flex-col items-center justify-center rounded-md border text-[10px] font-semibold transition-colors',
                isLandscape ? 'h-11 w-full' : 'h-11 w-11',
                i === idx ? 'border-primary bg-primary/20 text-primary'
                          : hasClean ? 'border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10'
                          : hasOrig ? 'border-amber-500/50 text-amber-400 hover:bg-amber-500/10'
                          : 'border-white/15 text-white/70 hover:bg-white/5',
              )}
              title={hasOrig && !hasClean ? `${s.label} — cutout processing…` : s.label}
            >
              {hasClean ? <Check className="h-4 w-4" /> : hasOrig ? <Sparkles className="h-3.5 w-3.5 animate-pulse" /> : i + 1}
              <span className="mt-0.5 max-w-[56px] truncate text-[9px] opacity-80">{s.key.replace(/_/g, ' ')}</span>
            </button>
          );
        })}
      </div>

      {/* Action controls */}
      <div className={cn(
        'flex shrink-0 items-center gap-3 bg-black/95 p-3',
        isLandscape ? 'w-28 flex-col justify-center' : 'safe-bottom',
      )}>
        <Button variant="outline" size="icon" disabled={idx === 0 || saving} onClick={() => { discardReview(); setIdx(idx - 1); }}>
          <ChevronLeft className="h-5 w-5" />
        </Button>
        {!reviewUrl ? (
          <>
            <Button onClick={capture} size="lg" className={cn('rounded-full p-0 shadow-lg', isLandscape ? 'h-14 w-14' : 'h-14 w-14 flex-1 max-w-14')}>
              <Camera className="h-6 w-6" />
            </Button>
            {!isLandscape && <div className="flex-1" />}
          </>
        ) : (
          <>
            <Button variant="outline" className={cn(isLandscape ? 'w-full' : 'flex-1')} onClick={discardReview} disabled={saving}>
              Retake
            </Button>
            <Button className={cn('gap-2', isLandscape ? 'w-full' : 'flex-1')} onClick={confirm} disabled={saving}>
              <Check className="h-5 w-5" /> {saving ? 'Saving…' : 'Use'}
            </Button>
          </>
        )}
        <Button variant="outline" size="icon" disabled={idx === slots.length - 1 || saving} onClick={() => { discardReview(); setIdx(idx + 1); }}>
          <ChevronRight className="h-5 w-5" />
        </Button>
      </div>
    </div>
  );
}

function OrientationIcon({ landscape }: { landscape: boolean }) {
  return landscape ? (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="8" y="3" width="8" height="18" rx="2" />
      <circle cx="12" cy="18" r=".5" fill="currentColor" />
    </svg>
  ) : (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="8" width="18" height="8" rx="2" />
      <circle cx="18" cy="12" r=".5" fill="currentColor" />
    </svg>
  );
}

function dataUriToBlob(uri: string): Blob {
  const [meta, b64] = uri.split(',', 2);
  const mime = /data:([^;]+);/.exec(meta ?? '')?.[1] ?? 'image/jpeg';
  const bin = atob(b64 ?? '');
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return new Blob([arr], { type: mime });
}

/**
 * Center-crop a JPEG data URI to the requested aspect ratio (target = w/h).
 * Used to enforce the photoAspect (landscape vs portrait) without messing
 * with the camera's native videoConstraints.
 */
function cropToAspect(dataUri: string, target: number): Promise<string> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const w = img.naturalWidth;
      const h = img.naturalHeight;
      const current = w / h;
      if (Math.abs(current - target) < 0.01) return resolve(dataUri);
      let cw = w, ch = h;
      if (current > target) {
        cw = Math.round(h * target);
      } else {
        ch = Math.round(w / target);
      }
      const sx = Math.round((w - cw) / 2);
      const sy = Math.round((h - ch) / 2);
      const canvas = document.createElement('canvas');
      canvas.width = cw;
      canvas.height = ch;
      const ctx = canvas.getContext('2d');
      if (!ctx) return resolve(dataUri);
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, sx, sy, cw, ch, 0, 0, cw, ch);
      resolve(canvas.toDataURL('image/jpeg', 0.92));
    };
    img.onerror = () => resolve(dataUri);
    img.src = dataUri;
  });
}

/**
 * Crop a JPEG data URI to the centered region matching `zoom` and rescale
 * back to the original dimensions. Used when CSS-zoom is in play and we
 * need the saved photo to match what the user saw on the preview.
 */
function cropDataUri(dataUri: string, zoom: number): Promise<string> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const w = img.naturalWidth;
      const h = img.naturalHeight;
      const cw = w / zoom;
      const ch = h / zoom;
      const sx = (w - cw) / 2;
      const sy = (h - ch) / 2;
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) return resolve(dataUri);
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, sx, sy, cw, ch, 0, 0, w, h);
      resolve(canvas.toDataURL('image/jpeg', 0.92));
    };
    img.onerror = () => resolve(dataUri);
    img.src = dataUri;
  });
}
